import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import ShieldIcon from "@mui/icons-material/Shield";
import { Box, Typography } from "@mui/material";
import { useState } from "react";

import { RiskDimensionId, RiskTypeDetail } from "../../../../types/risk-analysis";
import {
  getLevelFromScore,
  getRiskLevelColor,
  getScoreColor,
  MAX_COLLAPSED_JUSTIFICATION_LENGTH,
  SCORE_SEGMENTS,
  UNFILLED_COLOR
} from "./constants";
import EvidenceList from "./EvidenceList";
import ExpandIcon from "./ExpandIcon";

interface RiskDimensionCardProps {
  dimensionId: RiskDimensionId;
  name: string;
  data: RiskTypeDetail;
}

const DIMENSION_ICONS: Record<RiskDimensionId, React.ReactNode> = {
  financial: <AttachMoneyIcon sx={{ color: "#6b7280", fontSize: 18 }} />,
  compliance: <ShieldIcon sx={{ color: "#6b7280", fontSize: 18 }} />,
  operational: <SettingsIcon sx={{ color: "#6b7280", fontSize: 18 }} />,
  reputational: <PeopleIcon sx={{ color: "#6b7280", fontSize: 18 }} />
};

const RiskDimensionCard = ({ dimensionId, name, data }: RiskDimensionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const scoreColor = getScoreColor(data.score);
  const level = getLevelFromScore(data.score);
  const levelColor = getRiskLevelColor(level);
  const needsTruncation = data.justification.length > MAX_COLLAPSED_JUSTIFICATION_LENGTH;

  return (
    <Box
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow:
            "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)"
        }
      }}
    >
      <Box sx={{ bgcolor: "#f9fafb", p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
            {DIMENSION_ICONS[dimensionId]}
            <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "14px" }}>
              {name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "3px" }}>
            {SCORE_SEGMENTS.map((segment) => (
              <Box
                key={segment}
                sx={{
                  width: "12px",
                  height: "12px",
                  bgcolor: segment <= data.score ? scoreColor : UNFILLED_COLOR,
                  borderRadius: "2px"
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        onClick={() => setIsExpanded((prev) => !prev)}
        sx={{
          bgcolor: "white",
          p: 2,
          borderTop: "1px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          minHeight: "120px",
          "&:hover": { bgcolor: "#f9fafb" }
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#374151",
              lineHeight: 1.5,
              flex: 1,
              ...(!isExpanded && needsTruncation
                ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }
                : {})
            }}
          >
            {data.justification}
          </Typography>
          {needsTruncation && (
            <ExpandIcon
              isExpanded={isExpanded}
              sx={{ flexShrink: 0, mt: 0.5, color: "#9ca3af", fontSize: 16 }}
            />
          )}
        </Box>

        {isExpanded && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1
              }}
            >
              Evidence
            </Typography>
            <EvidenceList items={data.evidence} bulletColor={levelColor} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RiskDimensionCard;
